import { useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import Api, { DEFAULT_ERROR_MESSAGE } from '../../../../api/Api';
import { GLOBAL_HD_DTU_ID } from '../../../../utils/common';
import {
  trackUpdate,
  EVENT_ANALYTICS_TYPE_UPDATED,
} from '../../../../utils/matomoEvents';
import { BASIC_ANALYTICS, RosePlotChart } from '../../../../types/analytics';
import { IGetRosePlotV2ApiPayload } from '../../../../types/chart';

interface RosePlotLogicProps {
  coordinates: number[];
  timeStepInterval: string;
  pointLabel: string;
  isFwe?: boolean;
  fwePointName?: string;
  setRosePlotPreview: (data: any) => void;
}

export const useRosePlot = ({
  coordinates,
  timeStepInterval,
  pointLabel,
  isFwe,
  fwePointName,
  setRosePlotPreview,
}: RosePlotLogicProps) => {
  const [updateRosePlotData, setUpdatedRosePlotData] =
    useState<RosePlotChart | null>(null);
  const [errorRosePlot, setErrorRosePlot] = useState('');
  const pointName = isFwe ? fwePointName ?? pointLabel : pointLabel;
  const abortControllerRef = useRef<AbortController | null>(null);

  const rosePlotMutation = useMutation({
    mutationFn: async (
      values: IGetRosePlotV2ApiPayload & { signal?: AbortSignal }
    ) => {
      const {
        orderId,
        datasetName,
        startDate,
        endDate,
        directionalDataset,
        month,
        legendInterval,
        valueColumnName,
        directionColumnName,
        degreeInterval,
        signal,
      } = values;

      const data = await Api.chart.getRosePlotV2(
        orderId || '',
        pointName,
        datasetName,
        startDate,
        endDate,
        coordinates.join(),
        datasetName === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
        directionalDataset ?? '',
        month ?? 0,
        legendInterval,
        valueColumnName,
        directionColumnName,
        degreeInterval,
        { signal }
      );

      return { data, datasetName };
    },
    onSuccess: ({ data, datasetName }) => {
      if (data?.Data) {
        setUpdatedRosePlotData(data);
        trackUpdate(
          EVENT_ANALYTICS_TYPE_UPDATED,
          coordinates,
          datasetName,
          BASIC_ANALYTICS.ROSE_PLOT.toLowerCase()
        );
        setErrorRosePlot('');
      } else {
        setRosePlotPreview(null);
        setUpdatedRosePlotData(null);
      }
    },
    onError: (error: any) => {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        setUpdatedRosePlotData(null);
        setRosePlotPreview(null);
        return;
      }
      setErrorRosePlot(error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE);
      setUpdatedRosePlotData(null);
    },
  });

  const onUpdateRosePlot = useCallback(
    async (values: any) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      rosePlotMutation.mutate({
        ...values,
        signal: controller.signal,
      });
    },
    [rosePlotMutation]
  );

  const cancelRosePlot = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const resetRosePlot = () => {
    setUpdatedRosePlotData(null);
    setErrorRosePlot('');
  };

  return {
    updateRosePlotData,
    setUpdatedRosePlotData,
    errorRosePlot,
    resetRosePlot,
    onUpdateRosePlot,
    cancelRosePlot,
    loadingRosePlot: rosePlotMutation.isPending,
  };
};
